import { ButtonClickEvent, FormSubmitEvent, PageViewEvent } from '../types/analytics';

const CONSENT_KEY = 'ga_consent';
const MEASUREMENT_ID = window.__ENV__?.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GA_MEASUREMENT_ID;

class AnalyticsService {
  private initialized = false;
  private scriptLoaded = false;

  hasConsent(): boolean {
    const consent = localStorage.getItem(CONSENT_KEY);
    return consent === 'true';
  }

  setConsent(consent: boolean): void {
    localStorage.setItem(CONSENT_KEY, consent.toString());
    if (consent) {
      this.initialize();
    }
  }

  private loadGtagScript(): Promise<void> {
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (!MEASUREMENT_ID) {
        console.warn('GA4 Measurement ID not configured');
        reject(new Error('GA4 Measurement ID not configured'));
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Google Analytics script'));
      };
      document.head.appendChild(script);
    });
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (!this.hasConsent()) {
      console.log('GA4: User consent not granted');
      return;
    }

    if (!MEASUREMENT_ID) {
      console.warn('GA4: Measurement ID not configured');
      return;
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag(...args: any[]) {
        window.dataLayer?.push(args);
      };

      await this.loadGtagScript();

      window.gtag('js', new Date());
      window.gtag('config', MEASUREMENT_ID, {
        send_page_view: false,
      });

      this.initialized = true;
    } catch (error) {
      console.error('GA4: Failed to initialize', error);
    }
  }

  trackPageView(pageView: PageViewEvent): void {
    if (!this.initialized || !this.hasConsent()) {
      return;
    }

    if (!window.gtag) {
      console.warn('GA4: gtag not available');
      return;
    }

    window.gtag('event', 'page_view', {
      page_path: pageView.page_path,
      page_title: pageView.page_title || document.title,
    });
  }

  trackButtonClick(event: ButtonClickEvent): void {
    if (!this.initialized || !this.hasConsent()) {
      return;
    }

    if (!window.gtag) {
      console.warn('GA4: gtag not available');
      return;
    }

    window.gtag('event', 'button_click', {
      ...event,
    });
  }

  trackFormSubmit(event: FormSubmitEvent): void {
    if (!this.initialized || !this.hasConsent()) {
      return;
    }

    if (!window.gtag) {
      console.warn('GA4: gtag not available');
      return;
    }

    window.gtag('event', 'form_submit', {
      ...event,
    });
  }

  trackEvent(eventName: string, params?: Record<string, any>): void {
    if (!this.initialized || !this.hasConsent()) {
      return;
    }

    if (!window.gtag) {
      console.warn('GA4: gtag not available');
      return;
    }

    window.gtag('event', eventName, params);
  }
}

export const analytics = new AnalyticsService();
